import { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { DETAIL_LOAD_MORE_ROW_THRESHOLD } from '../../../../constants';
import { getDetailPaginatorTotal } from '../utils/getDetailPaginatorTotal';

type UseDetailStepNavigationParams = {
  rows: any[];
  dataKey: string;
  totalRowsCount: number;
  currentSelectedRowIndex: number;
  showSelectedRows: boolean;
  selectedVirtualRows: number;
  loadMoreData: () => void;
  setExpandedRow: (value: any) => void;
  setCurrentSelectedRowIndex: (value: number) => void;
  buildDetailUrlForRow: (newItemId: string) => string;
};

// Detail paginator stepping. Steps can point past the loaded rows: without a
// selection because the dataset is lazy, in selection mode because select-all
// keeps the rows beyond the loaded page as a count only (selectedVirtualRows).
// Such a step is parked until the batch that materializes its row arrives.
// Owns totalSteps too — the paginator label and the stepping bound must be the
// same number.
export const useDetailStepNavigation = ({
  rows,
  dataKey,
  totalRowsCount,
  currentSelectedRowIndex,
  showSelectedRows,
  selectedVirtualRows,
  loadMoreData,
  setExpandedRow,
  setCurrentSelectedRowIndex,
  buildDetailUrlForRow,
}: UseDetailStepNavigationParams) => {
  const history = useHistory();
  const pendingRowIndexRef = useRef<number | null>(null);
  const canLoadMore = !showSelectedRows || selectedVirtualRows > 0;
  const totalSteps = getDetailPaginatorTotal(
    rows.length,
    selectedVirtualRows,
    showSelectedRows,
    totalRowsCount
  );

  const goToRowIndex = useCallback(
    (index: number) => {
      const row = rows[index];

      if (!row) {
        // Nothing more can arrive (fully materialized selection) — no step.
        if (!canLoadMore) return;
        pendingRowIndexRef.current = index;
        loadMoreData();
        return;
      }

      pendingRowIndexRef.current = null;
      setExpandedRow(row);
      setCurrentSelectedRowIndex(index);
      history.push(buildDetailUrlForRow(row[dataKey]));

      if (canLoadMore && index > rows.length - DETAIL_LOAD_MORE_ROW_THRESHOLD) {
        loadMoreData();
      }
    },
    [
      rows,
      dataKey,
      canLoadMore,
      history,
      loadMoreData,
      setExpandedRow,
      setCurrentSelectedRowIndex,
      buildDetailUrlForRow,
    ]
  );

  const previousDetail = () => {
    if (currentSelectedRowIndex > 0) {
      goToRowIndex(currentSelectedRowIndex - 1);
    }
  };

  const nextDetail = () => {
    if (currentSelectedRowIndex < totalSteps - 1) {
      goToRowIndex(currentSelectedRowIndex + 1);
    }
  };

  // Batch arrived — finish the parked step.
  useEffect(() => {
    const pendingRowIndex = pendingRowIndexRef.current;
    if (pendingRowIndex === null || !rows[pendingRowIndex]) return;

    goToRowIndex(pendingRowIndex);
  }, [rows, goToRowIndex]);

  return { previousDetail, nextDetail, totalSteps };
};
